import { Fade, makeStyles, List, Paper, Typography } from "@material-ui/core";
import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { SiteContext } from "../contexts/SiteContext";
import fetchSite from "../utils/fetchSite";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminBar from "../components/AdminBar";
import EntryResult from "../components/EntryResult";
import PersonResult from "../components/PersonResult";
import Banner from "../components/Banner";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    paddingBottom: "4rem",
  },
  main: {
    margin: "0 auto",
    padding: "1rem",
    paddingTop: 0,
    maxWidth: process.env.REACT_APP_MAX_WIDTH,
  },
  paper: {
    padding: "1rem",
  },
}));

function Search() {
  const classes = useStyles();
  const { selectedSite, setSelectedSite } = useContext(SiteContext);
  const { query, siteSlug } = useParams();

  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Load the site based on :siteSlug
  useEffect(() => {
    fetchSite(siteSlug, setSelectedSite);
  }, [siteSlug, setSelectedSite]);

  // Fetch search results from the API
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query || !selectedSite) {
        return;
      }

      setIsSearching(true);
      setHasSearched(false);

      try {
        let res = await fetch(
          `${process.env.REACT_APP_API_URL}/search/${encodeURIComponent(
            query
          )}?site=${encodeURIComponent(selectedSite.slug)}`
        );
        res = await res.json();

        const data = Array.isArray(res.data) ? res.data : [];
        setResults(data);
      } catch (err) {
        console.error("Error fetching search results:", err);
        setResults([]);
      } finally {
        setIsSearching(false);
        setHasSearched(true);
      }
    };

    fetchSearchResults();
  }, [query, selectedSite]);

  // Keep only entries that belong to the current site's units.
  // People should already be filtered by the backend.
  useEffect(() => {
    if (!selectedSite) {
      return;
    }

    const siteUnitIds = (selectedSite.units || []).map((u) =>
      typeof u === "object" && u !== null ? u._id : u
    );

    const newResults = (results || []).filter((item) => {
      if (!item || !item.doc) return false;

      const { type, doc } = item;

      if (type === "entry") {
        const unitId =
          doc.unit && typeof doc.unit === "object" ? doc.unit._id : doc.unit;

        if (!unitId) return false;

        return siteUnitIds.includes(unitId);
      }

      if (type === "person") {
        return true;
      }

      return false;
    });

    setFilteredResults(newResults);
  }, [results, selectedSite]);

  if (!selectedSite) {
    return <Loading />;
  }

  let resultsText = "";
  if (isSearching) {
    resultsText = "Searching...";
  } else if (hasSearched) {
    resultsText =
      filteredResults.length === 1
        ? "1 result found"
        : `${filteredResults.length} results found`;
  }

  return (
    <Fade in={typeof filteredResults !== "undefined"}>
      <div className={classes.root}>
        <AdminBar />
        <Header />
        <AppBar />
        <Banner />
        <Breadcrumbs />
        <main className={classes.main}>
          <Paper className={classes.paper}>
            <Typography component="h1" variant="h5">
              Search
            </Typography>
            {resultsText && (
              <Typography color="primary">{resultsText}</Typography>
            )}
            <List className={classes.list}>
              {!isSearching &&
                hasSearched &&
                filteredResults.map((item, i) => {
                  const { type, doc } = item;

                  if (type === "entry") {
                    return (
                      <EntryResult
                        key={doc._id || i}
                        result={doc}
                        index={i}
                      />
                    );
                  }

                  if (type === "person") {
                    return (
                      <PersonResult
                        key={doc._id || i}
                        result={doc}
                        index={i}
                      />
                    );
                  }

                  return null;
                })}
            </List>
          </Paper>
        </main>
      </div>
    </Fade>
  );
}

export default Search;
